import { ISetPiece, SetPieceShape } from '../models/SetPieces';
import { IAudioClip } from '../models/Timeline';
import { RootState } from '../state';
import {
	inverseLerp,
	lerp,
	ovalLineSegmentIntersection,
	rectangleLineSegmentIntersection,
	triangleLineSegmentIntersection,
} from './geometry';
import { ReverbEffect } from './reverbEffect';
import { IVec2, Vec2 } from './vec2';

const intersectionTestsPerSecond = 10;
const setPieceIntersectionGain = 0.2; // The volume of a dancer when a setPiece is between them and the audience

export async function startTimeline(
	state: RootState,
	audioContext: BaseAudioContext,
	rootNode: AudioNode,
	timelineTime: number,
	getAudioBufferFunc: (audioFileId: number) => Promise<AudioBuffer | undefined>
): Promise<ReverbEffect> {
	const listener = audioContext.listener;

	const audience = state.undoable.setPieces.audience;
	const radians = (-audience.rotation + 90) * (Math.PI / 180);

	if (listener.forwardX !== undefined) {
		listener.forwardX.value = Math.cos(radians);
		listener.forwardY.value = Math.sin(radians);
		listener.forwardZ.value = 0;
		listener.upX.value = 0;
		listener.upY.value = 0;
		listener.upZ.value = 1;
	} else if (listener.setOrientation !== undefined) {
		// This is for Firefox
		listener.setOrientation(Math.cos(radians), Math.sin(radians), 0, 0, 0, 1);
	}

	if (listener.positionX !== undefined) {
		listener.positionX.value = audience.pos.x;
		listener.positionY.value = audience.pos.y;
		listener.positionZ.value = audience.height;
	} else if (listener.setPosition !== undefined) {
		// This is for Firefox
		listener.setPosition(audience.pos.x, audience.pos.y, audience.height);
	}

	const audioContextTime = audioContext.currentTime;

	// Reverb effect
	const reverbEffect = new ReverbEffect(
		audioContext,
		state.undoable.acousticSettings.reverbSettings
	);
	reverbEffect.connect(rootNode);

	const timeline = state.undoable.timeline;
	const staticTracks = [timeline.musicTrack, timeline.ambienceTrack, timeline.foleyTrack];
	for (const track of staticTracks) {
		if (track.muted || track.clips.length === 0) continue;
		for (const clip of track.clips) {
			await startAudioClip(
				audioContext,
				clip,
				reverbEffect.inputNode,
				timelineTime,
				audioContextTime,
				getAudioBufferFunc
			);
		}
	}

	for (const dancer of timeline.dancers) {
		let dancerNode = audioContext.createGain();
		dancerNode.connect(reverbEffect.inputNode);

		if (dancer.pathNodes.length > 0) {
			// Check along the dancer's path and adjust the volume based on
			// if there are any set pieces between the audience and the dancer
			const setPieces = state.undoable.setPieces.setPieces;
			let isFirstIntersectionTest = true;
			let intersection = false;
			for (let i = 1; i < dancer.pathNodes.length; i++) {
				const prevPathNode = dancer.pathNodes[i - 1];
				const nextPathNode = dancer.pathNodes[i];

				const startTime = prevPathNode.time + prevPathNode.delay;
				const endTime = nextPathNode.time;
				if (endTime < timelineTime) continue;

				const intersectionTestCount = Math.ceil(
					(endTime - startTime) * intersectionTestsPerSecond
				);

				for (let i = 0; i < intersectionTestCount + 1; i++) {
					const t = intersectionTestCount > 0 ? i / intersectionTestCount : 1.0;
					const time = lerp(startTime, endTime, t);
					if (time < timelineTime) continue;

					const pos = Vec2.lerp(prevPathNode.pos, nextPathNode.pos, t);

					intersection = setPiecesLineSegmentIntersection(
						setPieces,
						audience.pos,
						pos
					);

					if (isFirstIntersectionTest) {
						dancerNode.gain.setValueAtTime(
							intersection ? setPieceIntersectionGain : 1.0,
							audioContextTime
						);
						isFirstIntersectionTest = false;
					}

					dancerNode.gain.linearRampToValueAtTime(
						intersection ? setPieceIntersectionGain : 1.0,
						time - timelineTime + audioContextTime
					);
				}
			}

			// Mute after last node
			const lastPathNode = dancer.pathNodes[dancer.pathNodes.length - 1];
			if (lastPathNode.delay > 0) {
				// Hold the volume until the end of the delay of the last node
				dancerNode.gain.linearRampToValueAtTime(
					intersection ? setPieceIntersectionGain : 1.0,
					lastPathNode.time + lastPathNode.delay + 0.1 - timelineTime + audioContextTime
				);
			}
			dancerNode.gain.linearRampToValueAtTime(
				0.0,
				lastPathNode.time + lastPathNode.delay + 0.1 - timelineTime + audioContextTime
			);
		}

		const trackInfos = [
			{ track: dancer.aboveHeadTrack, heightMultiplier: 1.2 },
			{ track: dancer.headTrack, heightMultiplier: 1.0 },
			{ track: dancer.torsoTrack, heightMultiplier: 0.6 },
			{ track: dancer.legsTrack, heightMultiplier: 0.2 },
			{ track: dancer.groundTrack, heightMultiplier: 0.0 },
		];

		for (const trackInfo of trackInfos) {
			const track = trackInfo.track;
			const trackHeight = dancer.height * trackInfo.heightMultiplier;
			if (track.muted || track.clips.length === 0) continue;

			let trackNode;
			if (dancer.pathNodes.length > 0) {
				let pannerNode = new PannerNode(audioContext, {
					panningModel: 'HRTF',
					distanceModel: 'inverse',
					refDistance: 100,
					positionX: dancer.pathNodes[0].pos.x,
					positionY: dancer.pathNodes[0].pos.y,
					positionZ: trackHeight,
				});

				let nodeIdx = 0;
				// Advance past nodes that are behind us on the timeline
				while (nodeIdx < dancer.pathNodes.length) {
					if (dancer.pathNodes[nodeIdx].time > timelineTime) break;
					nodeIdx++;
				}

				if (nodeIdx === 0) {
					pannerNode.positionX.setValueAtTime(
						dancer.pathNodes[0].pos.x,
						audioContextTime
					);
					pannerNode.positionY.setValueAtTime(
						dancer.pathNodes[0].pos.y,
						audioContextTime
					);
				} else if (nodeIdx === dancer.pathNodes.length) {
					pannerNode.positionX.setValueAtTime(
						dancer.pathNodes[nodeIdx - 1].pos.x,
						audioContextTime
					);
					pannerNode.positionY.setValueAtTime(
						dancer.pathNodes[nodeIdx - 1].pos.y,
						audioContextTime
					);
				} else {
					const prevPathNode = dancer.pathNodes[nodeIdx - 1];
					const nextPathNode = dancer.pathNodes[nodeIdx];

					if (timelineTime < prevPathNode.time + prevPathNode.delay) {
						pannerNode.positionX.setValueAtTime(prevPathNode.pos.x, audioContextTime);
						pannerNode.positionY.setValueAtTime(prevPathNode.pos.y, audioContextTime);
						pannerNode.positionX.linearRampToValueAtTime(
							prevPathNode.pos.x,
							prevPathNode.time + prevPathNode.delay - timelineTime + audioContextTime
						);
						pannerNode.positionY.linearRampToValueAtTime(
							prevPathNode.pos.y,
							prevPathNode.time + prevPathNode.delay - timelineTime + audioContextTime
						);
					} else {
						let t = inverseLerp(
							prevPathNode.time + prevPathNode.delay,
							nextPathNode.time,
							timelineTime
						);
						let pos = Vec2.lerp(prevPathNode.pos, nextPathNode.pos, t);
						pannerNode.positionX.setValueAtTime(pos.x, audioContextTime);
						pannerNode.positionY.setValueAtTime(pos.y, audioContextTime);
					}
				}

				// Set up positions for the future path nodes
				while (nodeIdx < dancer.pathNodes.length) {
					const pathNode = dancer.pathNodes[nodeIdx];

					pannerNode.positionX.linearRampToValueAtTime(
						pathNode.pos.x,
						pathNode.time - timelineTime + audioContextTime
					);
					pannerNode.positionY.linearRampToValueAtTime(
						pathNode.pos.y,
						pathNode.time - timelineTime + audioContextTime
					);

					if (pathNode.delay > 0) {
						pannerNode.positionX.linearRampToValueAtTime(
							pathNode.pos.x,
							pathNode.time + pathNode.delay - timelineTime + audioContextTime
						);
						pannerNode.positionY.linearRampToValueAtTime(
							pathNode.pos.y,
							pathNode.time + pathNode.delay - timelineTime + audioContextTime
						);
					}

					nodeIdx++;
				}

				pannerNode.connect(dancerNode);
				trackNode = pannerNode;
			} else {
				trackNode = dancerNode;
			}

			for (const clip of track.clips) {
				await startAudioClip(
					audioContext,
					clip,
					trackNode,
					timelineTime,
					audioContextTime,
					getAudioBufferFunc
				);
			}
		}
	}

	return reverbEffect;
}

// Start playing a timeline audio clip at the right time
async function startAudioClip(
	audioContext: BaseAudioContext,
	clip: IAudioClip,
	connectNode: AudioNode,
	timelineTime: number,
	audioContextTime: number,
	getAudioBufferFunc: (audioFileId: number) => Promise<AudioBuffer | undefined>
) {
	const audioBuffer = await getAudioBufferFunc(clip.audioFileId);
	if (audioBuffer === undefined) {
		console.error('Could not find audio buffer with audioFileId: ' + clip.audioFileId);
		return;
	}

	if (clip.time + clip.duration <= timelineTime) {
		// We are completely past this clip on the timeline, don't need to start it
		return;
	}

	let source = audioContext.createBufferSource();
	source.buffer = audioBuffer;
	source.connect(connectNode);

	if (clip.time >= timelineTime) {
		source.start(clip.time - timelineTime + audioContextTime, 0, clip.duration);
	} else {
		const offset = timelineTime - clip.time;
		source.start(audioContextTime, offset, clip.duration - offset);
	}
}

function setPiecesLineSegmentIntersection(setPieces: ISetPiece[], start: IVec2, end: IVec2) {
	for (const setPiece of setPieces) {
		switch (setPiece.shape) {
			case SetPieceShape.Rectangle: {
				const intersectionFound = rectangleLineSegmentIntersection(
					{ center: setPiece.pos, size: setPiece.size, rotation: setPiece.rotation },
					start,
					end
				);
				if (intersectionFound) return true;
				break;
			}
			case SetPieceShape.Oval: {
				const intersectionFound = ovalLineSegmentIntersection(
					{ center: setPiece.pos, size: setPiece.size, rotation: setPiece.rotation },
					start,
					end
				);
				if (intersectionFound) return true;
				break;
			}
			case SetPieceShape.Triangle: {
				const intersectionFound = triangleLineSegmentIntersection(
					{ center: setPiece.pos, size: setPiece.size, rotation: setPiece.rotation },
					start,
					end
				);
				if (intersectionFound) return true;
				break;
			}
		}
	}

	return false;
}
